import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import {
  type FilterSelectionPart,
  formatFilterSelection,
} from './filter-selection-summary';

/** `cancelled`: every subscriber left before a result arrived, so the fetch was aborted. */
type InspectorRecordStatus = 'pending' | 'success' | 'error' | 'cancelled';

export type InspectorRecord = {
  /** Stable per attempt; urql's operation key repeats across re-runs. */
  id: string;
  operationKey: number;
  operationName: string;
  /**
   * The top-level GraphQL fields the operation selects — `pay_v3` for
   * `GetPayV3`. This is what the row is named after and what the API-docs link
   * is keyed on, because the document name is a frontend-only handle that means
   * nothing to anyone reading the drawer from outside the team.
   *
   * Usually exactly one. Empty only if the document had no parseable operation
   * definition, in which case the drawer falls back to `operationName`.
   */
  rootFields: string[];
  /**
   * Route the request was fired from, e.g. `/company/overview`. Stamped at
   * capture rather than read at render, because by the time you open the drawer
   * you have usually navigated somewhere else.
   */
  route: string;
  /**
   * Stable key for the filter selection in force when the request fired.
   * Requests sharing one selection group together, so changing a filter starts
   * a visibly new batch instead of interleaving with the previous numbers.
   */
  filterSignature: string;
  /** That selection rendered as chips for the group heading. */
  filterParts: FilterSelectionPart[];
  kind: string;
  variables: Record<string, unknown>;
  query: string;
  /**
   * Every chart card fed by this request. More than one when urql dedupes a
   * shared (query, variables) pair across cards.
   */
  chartItemIds: string[];
  /**
   * `performance.now()` stamps, on the same clock as the page visit so the
   * drawer can lay the request out on the visit's timeline. `fetchStartedAt`
   * is absent for a cache hit; the other two until the result lands.
   */
  dispatchedAt: number;
  fetchStartedAt?: number;
  finishedAt?: number;
  /** Network round-trip, `fetchStartedAt` → `finishedAt`. */
  durationMs?: number;
  /** JSON length of the response, so the drawer can say how big it was. */
  responseBytes?: number;
  /**
   * The `x-request-id` the auth exchange stamped on the request — what Kibana
   * indexes the server-side logs by. Absent for a cache hit, which never
   * reached that exchange.
   */
  requestId?: string;
  /** Served by urql's cache — it never reached the network. */
  fromCache: boolean;
  /**
   * Times this page re-read the same request from the client cache after
   * fetching it. A re-read does not replace the row — the row IS the fetch,
   * and the drawer would otherwise claim the page never fetched at all — so
   * it is counted here instead.
   */
  cacheReads?: number;
  status: InspectorRecordStatus;
  error?: string;
  data?: unknown;
};

/**
 * One page visit, from navigation until its last loader cleared. Keyed by
 * route in the store, latest visit wins — the same identity the records group
 * under, so the figure sits on the page heading its requests are listed under.
 */
/**
 * One request as the visit saw it. Kept apart from `records`, which hold the
 * LATEST attempt per identity: a page that re-runs its queries after first
 * paint replaces the rows the visit was actually waiting on, and the timeline
 * has to keep showing those.
 */
export type VisitRequest = {
  id: string;
  operationKey: number;
  label: string;
  chartItemIds: string[];
  dispatchedAt: number;
  fetchStartedAt?: number;
  finishedAt?: number;
  fromCache: boolean;
};

/** Enough for a page that re-fetches everything a few times over. */
const MAX_VISIT_REQUESTS = 120;

export type PageLoadTiming = {
  /** `performance.now()` at the navigation. */
  startedAt: number;
  /** Every request dispatched while the visit was loading, in order. */
  requests: VisitRequest[];
  /**
   * `abandoned` means the viewer navigated on before the loaders cleared, so
   * there is no figure to show and never will be for this visit.
   */
  status: 'loading' | 'rendered' | 'abandoned';
  durationMs?: number;
};

/**
 * The logical request: the page, the filter selection in force, and urql's
 * (query, variables) key. Route and selection are part of it, not just the
 * grouping: without them the same query re-run under new filters would
 * replace the old row, and "before vs after I changed this filter" — the point
 * of splitting by selection — could never be compared. It is NOT the chart
 * ids: cards register one at a time as they mount, so an identity built from
 * them read `[A]`, `[A,B]`, `[A,B,C]` for one request and never superseded.
 */
const identityOf = (record: InspectorRecord) =>
  [record.route, record.filterSignature, record.operationKey].join('|');

/**
 * Newest-first cap. Responses are held by reference, which pins them past
 * urql's own cache eviction, so the cap is what keeps a long session bounded.
 */
const MAX_RECORDS = 60;

/**
 * Panel size bounds. The minimum width keeps the header controls on one row.
 * The stored frame is clamped again against the viewport at render, so a panel
 * placed on a large monitor is never stranded off-screen on a small one.
 */
const DEFAULT_WIDTH = 520;
export const MIN_WIDTH = 320;
export const MAX_WIDTH = 1200;
const DEFAULT_HEIGHT = 620;
export const MIN_HEIGHT = 200;
export const MAX_HEIGHT = 2000;

/**
 * Where the panel sits before it has ever been dragged. `null` rather than a
 * number because the default is viewport-relative and the store is created
 * before there is a viewport to measure.
 */
export type PanelFrame = {
  /** Distance from the viewport's left/top edge, or null for the default. */
  x: number | null;
  y: number | null;
  width: number;
  height: number;
};

type DataInspectorState = {
  /**
   * The user's own toggle. Persisted, so it survives a reload.
   *
   * This is a VISIBILITY switch, not a recording one — capture runs whenever
   * the viewer is permitted (see {@link isInspectorRecording}), so switching it
   * on reveals what has already been captured instead of an empty panel that
   * only fills after a reload.
   */
  enabled: boolean;
  /**
   * Whether the viewer is allowed the inspector at all. NOT persisted, and
   * pushed in by `DataInspectorAccessSync` — the store is read from the urql
   * exchange, which is created outside the React tree and cannot use hooks.
   */
  isPermitted: boolean;
  isOpen: boolean;
  /** When set, the drawer shows only this chart card's operations. */
  focusedChartItemId: string | null;
  /**
   * The filter selection in force right now. Pushed in by
   * `DataInspectorFilterSync` rather than read from context by the exchange,
   * which runs outside React — the same reason `isPermitted` is mirrored here.
   */
  currentFilterSignature: string;
  currentFilterParts: FilterSelectionPart[];
  /** Drag-set position and size. Persisted — it is a workspace preference. */
  frame: PanelFrame;
  /** Collapsed to its header, so it can be parked without losing its place. */
  isMinimised: boolean;
  records: InspectorRecord[];
  /**
   * Neither gated on the permission nor wiped by the kill switch, unlike
   * `records`: nothing in a timing is sensitive, and the first page after a
   * reload starts loading before the permission arrives —
   * `DataInspectorAccessSync` pushes `false` first while the admin check
   * resolves, which would wipe the one measurement a hard refresh is taken for.
   */
  pageLoads: Record<string, PageLoadTiming>;
  setEnabled: (enabled: boolean) => void;
  setPermitted: (isPermitted: boolean) => void;
  setFrame: (frame: Partial<PanelFrame>) => void;
  toggleMinimised: () => void;
  setFilterSelection: (signature: string, parts: FilterSelectionPart[]) => void;
  setOpen: (isOpen: boolean) => void;
  openForChart: (chartItemId: string | null) => void;
  setFocusedChart: (chartItemId: string | null) => void;
  startRecord: (record: InspectorRecord) => void;
  finishRecord: (id: string, patch: Partial<InspectorRecord>) => void;
  /** No-op unless the record is still pending. */
  cancelRecord: (id: string, at: number) => void;
  startPageLoad: (route: string, startedAt: number) => void;
  finishPageLoad: (route: string, durationMs: number) => void;
  clearRecords: () => void;
};

export const useDataInspectorStore = create<DataInspectorState>()(
  persist(
    (set, get) => ({
      enabled: false,
      isPermitted: false,
      isOpen: false,
      focusedChartItemId: null,
      currentFilterSignature: '',
      currentFilterParts: [],
      frame: {
        x: null,
        y: null,
        width: DEFAULT_WIDTH,
        height: DEFAULT_HEIGHT,
      },
      isMinimised: false,
      records: [],
      pageLoads: {},

      // Hiding the inspector deliberately KEEPS what was captured. Recording
      // does not depend on this toggle, so discarding on the way out would
      // reintroduce the reload it exists to avoid: switch off, switch back on,
      // and you would be staring at an empty panel until the page refetched.
      // `Clear` is the button for throwing records away.
      setEnabled: (enabled) =>
        set(
          enabled
            ? { enabled }
            : { enabled, isOpen: false, focusedChartItemId: null }
        ),

      // Losing the permission has to tear everything down, not just hide the
      // menu entry. This is the ONLY kill switch there is now that capture no
      // longer waits for the user's toggle: without it a non-admin session
      // would go on recording request bodies with nothing on screen to say so.
      setPermitted: (isPermitted) =>
        set(
          isPermitted
            ? { isPermitted }
            : {
                isPermitted,
                isOpen: false,
                records: [],
                focusedChartItemId: null,
              }
        ),

      // Size is clamped here; position is clamped at render, where the
      // viewport (and the panel's own size) are known.
      setFrame: (frame) =>
        set((state) => ({
          frame: {
            ...state.frame,
            ...frame,
            ...(frame.width !== undefined && {
              width: Math.min(Math.max(frame.width, MIN_WIDTH), MAX_WIDTH),
            }),
            ...(frame.height !== undefined && {
              height: Math.min(Math.max(frame.height, MIN_HEIGHT), MAX_HEIGHT),
            }),
          },
        })),

      toggleMinimised: () =>
        set((state) => ({ isMinimised: !state.isMinimised })),

      // The filter map gets a new identity far more often than the selection
      // changes, so this is called on most FilterProvider renders. Bailing on an
      // unchanged selection keeps that churn from re-rendering the drawer.
      // Compared on the rendered text, not the signature alone: the signature is
      // deliberately label-free, so labels sharpening from raw ids to real names
      // must still land.
      setFilterSelection: (currentFilterSignature, currentFilterParts) =>
        set((state) =>
          state.currentFilterSignature === currentFilterSignature &&
          formatFilterSelection(state.currentFilterParts) ===
            formatFilterSelection(currentFilterParts)
            ? state
            : { currentFilterSignature, currentFilterParts }
        ),

      setOpen: (isOpen) => set({ isOpen }),

      openForChart: (chartItemId) =>
        set({ isOpen: true, focusedChartItemId: chartItemId }),

      setFocusedChart: (chartItemId) =>
        set({ focusedChartItemId: chartItemId }),

      // Which attempt a row shows is decided when the result lands (see
      // `finishRecord`), because only then is it known whether the re-run was
      // a real refetch or a cache re-read. Until then the new attempt sits
      // beside the old row as "in flight".
      startRecord: (record) =>
        set((state) => {
          const records = [record, ...state.records].slice(0, MAX_RECORDS);

          const visit = state.pageLoads[record.route];
          if (visit?.status !== 'loading') return { records };
          const request: VisitRequest = {
            id: record.id,
            operationKey: record.operationKey,
            label:
              record.rootFields.length > 0
                ? record.rootFields.join(', ')
                : record.operationName,
            chartItemIds: record.chartItemIds,
            dispatchedAt: record.dispatchedAt,
            fromCache: record.fromCache,
          };
          return {
            records,
            pageLoads: {
              ...state.pageLoads,
              [record.route]: {
                ...visit,
                requests: [...visit.requests, request].slice(
                  -MAX_VISIT_REQUESTS
                ),
              },
            },
          };
        }),

      // One row per logical request, and the row is the attempt that says
      // the most: a refetch over the network replaces the earlier row (a page
      // re-runs its queries on every filter change, and twelve identical rows
      // bury the one that matters); a cache re-read of a request this page
      // already fetched is folded into that row as a count, because replacing
      // the fetch with "cache" told the reader the page never fetched at all;
      // and an attempt torn down before its result has nothing to add over a
      // finished one.
      finishRecord: (id, patch) =>
        set((state) => {
          const existing = state.records.find((record) => record.id === id);
          if (!existing) return state;
          const updated = { ...existing, ...patch };
          const identity = identityOf(updated);
          const earlier = state.records.find(
            (record) =>
              record.id !== id &&
              record.status !== 'pending' &&
              identityOf(record) === identity
          );

          const resolve = (): InspectorRecord[] => {
            if (
              updated.status === 'success' &&
              updated.fromCache &&
              earlier &&
              !earlier.fromCache
            ) {
              return state.records
                .filter((record) => record.id !== id)
                .map((record) =>
                  record.id === earlier.id
                    ? { ...record, cacheReads: (record.cacheReads ?? 0) + 1 }
                    : record
                );
            }
            if (updated.status === 'cancelled' && earlier) {
              return state.records.filter((record) => record.id !== id);
            }
            return state.records
              .map((record) => (record.id === id ? updated : record))
              .filter(
                (record) => record.id === id || identityOf(record) !== identity
              );
          };
          const records = resolve();

          // The visit's copy follows the result even once the page has
          // settled — a request dispatched during the load and answered after
          // is still part of what the load waited on.
          const visit = state.pageLoads[existing.route];
          const requestIndex =
            visit?.requests.findIndex((request) => request.id === id) ?? -1;
          if (!visit || requestIndex === -1) return { records };
          const requests = [...visit.requests];
          const request = requests[requestIndex];
          if (!request) return { records };
          requests[requestIndex] = {
            ...request,
            fetchStartedAt: updated.fetchStartedAt,
            finishedAt: updated.finishedAt,
            fromCache: updated.fromCache,
          };
          return {
            records,
            pageLoads: {
              ...state.pageLoads,
              [existing.route]: { ...visit, requests },
            },
          };
        }),

      cancelRecord: (id, at) => {
        const record = get().records.find((candidate) => candidate.id === id);
        if (record?.status !== 'pending') return;
        get().finishRecord(id, {
          status: 'cancelled',
          error: 'Every subscriber left before a result arrived',
          finishedAt: at,
        });
      },

      // Only one page can be loading at a time, so starting a visit settles
      // every other open one as abandoned — otherwise a page left mid-load
      // would read "rendering…" forever in the drawer.
      startPageLoad: (route, startedAt) =>
        set((state) => {
          const pageLoads: Record<string, PageLoadTiming> = {};
          for (const [key, timing] of Object.entries(state.pageLoads)) {
            pageLoads[key] =
              timing.status === 'loading'
                ? { ...timing, status: 'abandoned' }
                : timing;
          }
          pageLoads[route] = { startedAt, status: 'loading', requests: [] };
          return { pageLoads };
        }),

      // No-ops unless that route is still loading: a settle arriving after
      // Clear, or after a later navigation abandoned the visit, has nothing
      // to attach to.
      finishPageLoad: (route, durationMs) =>
        set((state) => {
          const timing = state.pageLoads[route];
          if (timing?.status !== 'loading') return state;
          return {
            pageLoads: {
              ...state.pageLoads,
              [route]: { ...timing, status: 'rendered', durationMs },
            },
          };
        }),

      clearRecords: () =>
        set({ records: [], pageLoads: {}, focusedChartItemId: null }),
    }),
    {
      name: 'revelio:data-inspector',
      storage: createJSONStorage(() => localStorage),
      // Only the mode survives a reload; captures belong to one page visit.
      partialize: (state) => ({
        enabled: state.enabled,
        frame: state.frame,
        isMinimised: state.isMinimised,
      }),
    }
  )
);

/**
 * Whether the inspector's UI should be showing — the menu toggle, the
 * indicator, the drawer, the per-chart "Inspect" action.
 *
 * `enabled` alone is a persisted user preference that outlives the permission,
 * so nothing should read it on its own.
 */
export const isInspectorActive = (state: DataInspectorState) =>
  state.isPermitted && state.enabled;

/**
 * Whether the recorder exchange should be capturing. Deliberately NOT
 * {@link isInspectorActive}.
 *
 * Capture is decoupled from the user's toggle because a chart only issues its
 * query once: switching the mode on afterwards used to leave the drawer empty
 * until the page was reloaded, since nothing re-dispatches just because a panel
 * opened. Recording for anyone permitted makes the toggle purely a visibility
 * control — switch it on and the requests behind the page you are looking at
 * are already there.
 *
 * The buffer is capped ({@link MAX_RECORDS}) and never persisted, so the cost
 * of running with the panel closed is a bounded number of held responses.
 */
export const isInspectorRecording = (state: DataInspectorState) =>
  state.isPermitted;

/**
 * Read the store outside React. The urql client is created outside the React
 * tree, so the recorder exchange cannot use hooks to find out whether it
 * should be capturing.
 */
export const getDataInspectorState = () => useDataInspectorStore.getState();
